import { useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from '@/enterprise/components/Skeleton';
import usePricingSuggestion from '@/enterprise/data/usePricingSuggestion';
import useDataDog from '@/reporting/DataDog';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { RateType, useOfferBuilderContext } from '../OfferBuilderContext';
import useEnablePricing from '../useEnablePricing';
import usePreferredRateEligibility, { PreferredStepSkipReason } from '../usePreferredRateEligibility';
import { getNameYourRateMarkets } from '../utils';
import PricingStrategyOptionsBase from './PricingStrategyOptionsBase';
import PricingStrategyOptionsNoPricing from './PricingStrategyOptionsNoPricing';

// Renders when the pricing suggestion service is unavailable — falls back to the no-pricing
// layout and notifies the parent that the preferred step should not show.
const PricingStrategyOptionsServiceError = ({
  onShowPreferredStep,
}: {
  onShowPreferredStep?: (args: PreferredStepCallbackArgs) => void;
}) => {
  const { addError } = useDataDog();

  useEffect(() => {
    onShowPreferredStep?.({ show: false, skipReason: 'pricing-disabled' });
  }, [onShowPreferredStep]);

  useEffect(() => {
    addError('Pricing Suggestions - Service Unavailable (Offer Builder)');
  }, [addError]);

  return <PricingStrategyOptionsNoPricing />;
};

const isValidRateType = (rateType: string | undefined): rateType is RateType => {
  return rateType === 'APR' || rateType === 'DISC';
};

interface PreferredStepCallbackArgs {
  show: boolean;
  skipReason: PreferredStepSkipReason | null;
}

const PricingStrategyOptionsComponent = ({
  onShowPreferredStep,
}: {
  onShowPreferredStep?: (args: PreferredStepCallbackArgs) => void;
}) => {
  const { dispatch, state } = useOfferBuilderContext();
  const { rateType, selected } = state;
  const { includePreferred } = usePreferredRateEligibility(state);
  const nameYourRateMarkets = useMemo(() => getNameYourRateMarkets(selected), [selected]);
  const pricingData = usePricingSuggestion({
    takerMarkets: nameYourRateMarkets,
    includePreferred,
  });
  const { showPreferredStep: showPreferredStepWithPricing, skipReason } = usePreferredRateEligibility(
    state,
    pricingData.data
  );

  // set rate type to overall optimal rate type if not already set
  useEffect(() => {
    if (
      !rateType &&
      pricingData.data?.overallOptimalRateType &&
      isValidRateType(pricingData.data.overallOptimalRateType)
    ) {
      dispatch({
        type: 'SET_RATE_TYPE',
        payload: pricingData.data.overallOptimalRateType,
      });
    }
  }, [rateType, pricingData.data?.overallOptimalRateType, dispatch]);

  // Notify parent of the resolved showPreferredStep once pricing data is available
  useEffect(() => {
    onShowPreferredStep?.({ show: showPreferredStepWithPricing, skipReason });
  }, [showPreferredStepWithPricing, skipReason, onShowPreferredStep]);

  const onRateTypeChange = (value: RateType) => {
    dispatch({
      type: 'CHANGE_RATE_TYPE_AND_CLEAR_STRATEGY',
      payload: {
        rateType: value,
        nameYourRateMarkets: getNameYourRateMarkets(selected),
      },
    });
  };

  return <PricingStrategyOptionsBase pricingData={pricingData.data} onRateTypeChange={onRateTypeChange} />;
};

const PricingStrategyOptions = ({
  onShowPreferredStep,
}: {
  onShowPreferredStep?: (args: PreferredStepCallbackArgs) => void;
}) => {
  const { t } = useTranslation();
  const enablePricing = useEnablePricing();

  // When pricing is disabled, use component without pricing data to avoid suspense boundary.
  // Preferred step requires pricing, so notify parent it should not show.
  useEffect(() => {
    if (!enablePricing) {
      onShowPreferredStep?.({ show: false, skipReason: 'pricing-disabled' });
    }
  }, [enablePricing, onShowPreferredStep]);

  // Stable reference so the error boundary doesn't remount on every parent re-render.
  const ErrorFallback = useCallback(
    () => <PricingStrategyOptionsServiceError onShowPreferredStep={onShowPreferredStep} />,
    [onShowPreferredStep]
  );

  if (!enablePricing) {
    return <PricingStrategyOptionsNoPricing />;
  }

  return (
    <QueryBoundaries
      ErrorComponent={ErrorFallback}
      LoadingComponent={() => (
        <>
          <div className="mb-2 flex flex-col justify-between gap-2 sm:flex-row sm:items-center sm:gap-4">
            <div>
              <div className="text-lg font-medium">{t('builder.selectYourRate')}</div>
              <div className="mt-0.5 text-sm font-medium text-blue-500">{t('builder.learnMoreAboutRateOptions')}</div>
            </div>
            <Skeleton className="h-10 w-1/3" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        </>
      )}
    >
      <PricingStrategyOptionsComponent onShowPreferredStep={onShowPreferredStep} />
    </QueryBoundaries>
  );
};

export default PricingStrategyOptions;
